'use client';

import { motion } from 'framer-motion';

interface WaveformBarsProps {
  /** Number of bars to render */
  bars?: number;
  /** Height in pixels */
  height?: number;
  /** Color — defaults to current text color */
  color?: string;
  /** Whether bars are animating */
  active?: boolean;
  /** Use green for audio elements */
  sonic?: boolean;
  /** Additional className */
  className?: string;
  /** Real-time frequency data from useAudioAnalyser (0-1 values). When provided, overrides generic animation. */
  analyserData?: number[];
}

export function WaveformBars({
  bars = 5,
  height = 24,
  color,
  active = true,
  sonic = false,
  className = '',
  analyserData,
}: WaveformBarsProps) {
  const barColor = color ?? (sonic ? '#22C55E' : 'currentColor');
  const hasRealData = analyserData && analyserData.length > 0;

  return (
    <div
      className={`flex items-center gap-[2px] ${className}`}
      style={{ height }}
      role="presentation"
      aria-hidden="true"
    >
      {Array.from({ length: bars }).map((_, i) => {
        const binIndex = hasRealData
          ? Math.floor((i / bars) * analyserData.length)
          : 0;
        const realScale = hasRealData
          ? Math.max(0.1, analyserData[binIndex])
          : undefined;

        return (
          <motion.div
            key={`bar-${i}`}
            className="rounded-full"
            style={{
              width: 3,
              height: '100%',
              backgroundColor: barColor,
              originY: 1,
            }}
            animate={
              hasRealData
                ? { scaleY: realScale }
                : active
                  ? { scaleY: [0.3, 1, 0.3] }
                  : { scaleY: 0.3 }
            }
            transition={
              hasRealData
                ? { type: 'spring', damping: 15, stiffness: 300 }
                : active
                  ? {
                      duration: 0.8 + i * 0.1,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: i * 0.12,
                    }
                  : { duration: 0.3 }
            }
          />
        );
      })}
    </div>
  );
}
